import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Lock,
  Loader2,
  LogOut,
  RefreshCw,
  Inbox,
  Download,
  CalendarDays,
  Sparkles,
  MailPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FollowUpDialog } from "@/components/FollowUpDialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ss_admin_token";

const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-700",
  contacted: "bg-amber-50 text-amber-700",
  confirmed: "bg-emerald-50 text-emerald-700",
  declined: "bg-slate-100 text-slate-500",
};

const DAY_MS = 86400000;

const pilotPhase = (start, end) => {
  if (!start || !end) return null;
  const now = new Date();
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  const total = Math.max(1, Math.round((e - s) / DAY_MS));
  const elapsed = Math.floor((now - s) / DAY_MS);
  const plural = (n) => (n === 1 ? "" : "s");
  if (elapsed < 0)
    return { label: `Starts in ${-elapsed} day${plural(-elapsed)}`, pct: 0, tone: "bg-blue-50 text-blue-700" };
  if (elapsed > total)
    return { label: `Ended ${elapsed - total} day${plural(elapsed - total)} ago — follow up`, pct: 100, tone: "bg-amber-50 text-amber-700" };
  return {
    label: `Day ${elapsed + 1} of ${total}`,
    pct: Math.min(100, Math.round((elapsed / total) * 100)),
    tone: "bg-emerald-50 text-emerald-700",
  };
};

const PilotTimeline = ({ applications, onUpdate }) => {
  const confirmed = applications.filter((a) => a.status === "confirmed");
  if (confirmed.length === 0) return null;
  return (
    <section
      data-testid="pilot-timeline"
      className="mb-8 rounded-lg border border-border bg-white p-6 md:p-8"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-accent">
          <CalendarDays className="h-4 w-4" />
        </span>
        <div>
          <h2 className="font-serif text-xl tracking-tight text-primary">Pilot timeline</h2>
          <p className="text-xs text-muted-foreground">
            Confirmed hotels and their pilot windows — set dates so follow-ups never slip.
          </p>
        </div>
      </div>
      <div className="mt-6 space-y-6">
        {confirmed.map((app) => {
          const phase = pilotPhase(app.pilot_start, app.pilot_end);
          return (
            <div
              key={app.id}
              data-testid={`timeline-row-${app.id}`}
              className="space-y-3 border-t border-border pt-5 first:border-t-0 first:pt-0"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-semibold text-primary">{app.hotel_name}</p>
                {phase && (
                  <span
                    data-testid={`timeline-phase-${app.id}`}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${phase.tone}`}
                  >
                    {phase.label}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <label className="flex items-center gap-2">
                  Start
                  <Input
                    type="date"
                    data-testid={`timeline-start-${app.id}`}
                    value={app.pilot_start || ""}
                    onChange={(e) => onUpdate(app.id, { pilot_start: e.target.value })}
                    className="h-8 w-40 text-xs"
                  />
                </label>
                <span aria-hidden>→</span>
                <label className="flex items-center gap-2">
                  End
                  <Input
                    type="date"
                    data-testid={`timeline-end-${app.id}`}
                    value={app.pilot_end || ""}
                    onChange={(e) => onUpdate(app.id, { pilot_end: e.target.value })}
                    className="h-8 w-40 text-xs"
                  />
                </label>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-accent transition-[width] duration-500"
                  style={{ width: `${phase ? phase.pct : 0}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin(res.data.token);
    } catch {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={handleSubmit}
        className="w-full max-w-sm rounded-lg border border-border bg-white p-8 shadow-sm space-y-6"
      >
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
            <Lock className="h-4 w-4 text-white" />
          </span>
          <div>
            <p className="font-serif text-lg font-semibold text-primary leading-tight">SecureStay Locks</p>
            <p className="text-xs text-muted-foreground">Operator admin</p>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="admin-email">Email</Label>
          <Input
            id="admin-email"
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="admin-password">Password</Label>
          <Input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <Button
          data-testid="admin-login-btn"
          type="submit"
          disabled={loading}
          className="w-full rounded-full bg-accent text-accent-foreground font-semibold hover:bg-blue-700 transition-colors"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analysingId, setAnalysingId] = useState(null);
  const [followUpApp, setFollowUpApp] = useState(null);

  const fetchApplications = useCallback(async (tok) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/applications`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setApplications(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Could not load applications");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchApplications(token);
  }, [token, fetchApplications]);

  const updateApplication = async (id, payload) => {
    try {
      const res = await axios.patch(`${API}/applications/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApplications((apps) => apps.map((a) => (a.id === id ? res.data : a)));
      toast.success("Saved");
    } catch {
      toast.error("Could not save changes");
    }
  };

  const generateInsights = async (id) => {
    setAnalysingId(id);
    try {
      const res = await axios.post(
        `${API}/applications/${id}/insights`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApplications((apps) => apps.map((a) => (a.id === id ? res.data : a)));
      toast.success("AI insight ready");
    } catch {
      toast.error("AI insight failed — try again");
    } finally {
      setAnalysingId(null);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/applications/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "pilot-applications.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <Login onLogin={setToken} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
              <Lock className="h-4 w-4 text-white" />
            </span>
            <div>
              <p className="font-serif text-lg font-semibold text-primary leading-tight">
                Pilot applications
              </p>
              <p className="text-xs text-muted-foreground">
                {applications.length} application{applications.length === 1 ? "" : "s"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="export-csv-btn"
              variant="outline"
              size="sm"
              onClick={exportCsv}
              className="rounded-full"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </Button>
            <Button
              data-testid="refresh-applications-btn"
              variant="outline"
              size="sm"
              onClick={() => fetchApplications(token)}
              className="rounded-full"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
            <Button
              data-testid="admin-logout-btn"
              variant="ghost"
              size="sm"
              onClick={logout}
              className="rounded-full"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <PilotTimeline applications={applications} onUpdate={updateApplication} />
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : applications.length === 0 ? (
          <div
            data-testid="empty-applications-state"
            className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-white py-20 text-muted-foreground"
          >
            <Inbox className="h-8 w-8" />
            <p className="text-sm">No applications yet. Share your landing page to start collecting leads.</p>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-white">
            <Table data-testid="applications-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Hotel</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Spaces</TableHead>
                  <TableHead>Cars/night</TableHead>
                  <TableHead>Applied</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applications.map((app) => (
                  <TableRow key={app.id} data-testid={`application-row-${app.id}`}>
                    <TableCell className="font-semibold text-primary">
                      {app.hotel_name}
                      {app.notes && (
                        <p className="mt-0.5 max-w-[200px] truncate text-xs font-normal text-muted-foreground">
                          {app.notes}
                        </p>
                      )}
                      <div className="mt-1.5 flex items-center gap-2">
                        <button
                          data-testid={`log-lost-lock-${app.id}`}
                          onClick={() => updateApplication(app.id, { locks_lost: (app.locks_lost || 0) + 1 })}
                          className="rounded-full border border-border px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground transition-colors hover:border-amber-400 hover:text-amber-600"
                        >
                          +1 lost lock
                        </button>
                        {(app.locks_lost || 0) > 0 && (
                          <span
                            data-testid={`lost-count-${app.id}`}
                            className="text-[11px] font-semibold text-amber-600"
                          >
                            {app.locks_lost} unreturned
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {app.ai_score ? (
                          <span
                            data-testid={`fit-score-${app.id}`}
                            className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                              app.ai_score >= 7
                                ? "bg-emerald-50 text-emerald-700"
                                : app.ai_score >= 4
                                  ? "bg-amber-50 text-amber-700"
                                  : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            Fit {app.ai_score}/10
                          </span>
                        ) : (
                          <button
                            data-testid={`analyse-btn-${app.id}`}
                            onClick={() => generateInsights(app.id)}
                            disabled={analysingId === app.id}
                            className="flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground transition-colors hover:border-blue-400 hover:text-accent"
                          >
                            {analysingId === app.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Sparkles className="h-3 w-3" />
                            )}
                            AI insight
                          </button>
                        )}
                        <button
                          data-testid={`draft-followup-${app.id}`}
                          onClick={() => setFollowUpApp(app)}
                          className="flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground transition-colors hover:border-blue-400 hover:text-accent"
                        >
                          <MailPlus className="h-3 w-3" /> Draft follow-up
                        </button>
                      </div>
                      {app.ai_summary && (
                        <p
                          data-testid={`ai-summary-${app.id}`}
                          className="mt-1.5 max-w-[260px] text-[11px] italic leading-relaxed text-muted-foreground"
                        >
                          {app.ai_summary}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{app.contact_name}</p>
                      <p className="text-xs text-muted-foreground">{app.email}</p>
                      <p className="text-xs text-muted-foreground">{app.phone}</p>
                    </TableCell>
                    <TableCell data-testid={`spaces-${app.id}`}>{app.parking_spaces}</TableCell>
                    <TableCell data-testid={`cars-${app.id}`}>{app.avg_cars_per_night}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(app.created_at).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={app.status}
                        onValueChange={(v) => updateApplication(app.id, { status: v })}
                      >
                        <SelectTrigger
                          data-testid={`status-select-${app.id}`}
                          className={`h-8 w-[130px] rounded-full text-xs font-semibold ${STATUS_STYLES[app.status]}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(STATUS_STYLES).map((s) => (
                            <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>
                              {s.charAt(0).toUpperCase() + s.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </main>

      <FollowUpDialog
        app={followUpApp}
        token={token}
        onClose={() => setFollowUpApp(null)}
        onUpdated={(updated) =>
          setApplications((apps) => apps.map((a) => (a.id === updated.id ? updated : a)))
        }
      />
    </div>
  );
}
